import { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { RefreshCw, AlertCircle, PanelRightClose, PanelRight, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useScreenerData } from '@/hooks/useScreenerData'
import { useScreenerTickerParam } from '@/hooks/useScreenerTickerParam'
import { useScreenerExchangeSelection } from '@/hooks/useScreenerExchangeSelection'
import { useScreenerFilterDefaults } from '@/hooks/useScreenerFilterDefaults'
import { useScreenerDiscoveryDefaults } from '@/hooks/useScreenerDiscoveryDefaults'
import { useScreenerFilterPresets } from '@/hooks/useScreenerFilterPresets'
import { useScreenerOrderbookPresets } from '@/hooks/useScreenerOrderbookPresets'
import { useScreenerChartPresets } from '@/hooks/useScreenerChartPresets'
import { PresetControls } from '@/components/common/PresetControls'
import { useScreenerPreferenceHydration } from '@/hooks/useScreenerPreferenceHydration'
import { defaultDiscoveryFilters, parseDiscoveryFilters } from '@/lib/screenerDiscoveryDefaults'
import { resolveScreenerPriceExchange } from '@/lib/screenerPriceExchange'
import { extractTickerFromPastedText } from '@/lib/screenerNavigation'
import { DEFAULT_ORDERBOOK_COMPRESSION, parseScreenerOrderbookPrefs, type ScreenerOrderbookPrefs } from '@/lib/screenerOrderbookPreferences'
import { ScreenerFilterControls } from '@/components/screener/ScreenerFilterControls'
import { useAuth } from '@/hooks/useAuth'
import { useChartAlerts } from '@/hooks/useChartAlerts'
import { PersonalChartAlerts } from '@/components/screener/PersonalChartAlerts'
import { useExchangeOptions } from '@/hooks/useExchangeOptions'
import { ChartGrid, ViewMode, TimeframeSelector, ScreenerDiscoveryPanel } from '@/components/screener'
import { ScreenerHistoryPanel } from '@/components/screener/ScreenerHistoryPanel'
import { ScreenerTickerPicker } from '@/components/screener/ScreenerTickerPicker'
import { CopyTickerButton } from '@/components/screener/CopyTickerButton'
import { ScreenerSidebar, type ScreenerSidebarTab } from '@/components/screener/ScreenerSidebar'
import { ScreenerWorkspace } from '@/components/screener/ScreenerWorkspace'
import { ScreenerOrderbookPanel } from '@/components/screener/ScreenerOrderbookPanel'
import type { OrderbookCompression } from '@/components/screener/orderbookCompression'
import { isFreshOI } from '@/components/screener/chartUtils'
import { FALLBACK_EXCHANGES } from '@/types'
import type { ScreenerDiscoveryFilters, ScreenerExchangeData } from '@/types'

const PERIODS = [
  { value: '5s', label: '5S' },
  { value: '1m', label: '1M' },
  { value: '5m', label: '5M' },
  { value: '30m', label: '30M' },
  { value: '1h', label: '1H' },
  { value: '4h', label: '4H' },
  { value: '1d', label: '1D' },
]

const LIVE_OI_STALE_AFTER_SECONDS = 10
const DEFAULT_MARKETS = ['spot', 'futures']

type LiveOIStatusSummary = {
  enabled: boolean
  requestedCount: number
  collectingCount: number
  healthy: string[]
  waiting: string[]
  unchanged: string[]
  issues: string[]
}

function displayExchangeList(exchanges: string[]): string {
  return exchanges.map((exchange) => exchange.toUpperCase()).join(', ')
}

function findExchangeData(
  exchanges: Record<string, ScreenerExchangeData>,
  exchange: string,
): ScreenerExchangeData | undefined {
  const wanted = exchange.toLowerCase()
  const entry = Object.entries(exchanges).find(([name]) => name.toLowerCase() === wanted)
  return entry?.[1]
}

function getScreenerStorageKeys(username?: string | null) {
  const normalized = (username || '').trim().toLowerCase() || 'anonymous'
  return {
    mode: `screener-view-mode:${normalized}`,
    chartOrder: `screener-chart-order:${normalized}`,
    priceExchange: `screener-price-exchange:${normalized}`,
    discoveryOpen: `screener-discovery-open:${normalized}`,
    sidebarTab: `screener-sidebar-tab:${normalized}`,
    panelWidths: `screener-panel-widths:${normalized}`,
    discoveryFilters: `screener-discovery-filters:${normalized}`,
    orderbookPanelHidden: `screener-orderbook-panel-hidden:${normalized}`,
    orderbookSources: `screener-orderbook-sources:${normalized}`,
    orderbookConfig: `screener-orderbook-config:${normalized}`,
  }
}

export default function Screener() {
  const { user } = useAuth()
  const storageKeys = useMemo(() => getScreenerStorageKeys(user?.username), [user?.username])
  const [orderbookPanelHidden, setOrderbookPanelHidden] = useState(true)
  const {
    tickers,
    hasLoadedTickers,
    activeTicker,
    activeTickerData,
    settings,
    isLoading,
    error,
    lastUpdated,
    addTicker,
    removeTicker,
    setActiveTicker,
    updateSettings,
    refresh,
  } = useScreenerData(!orderbookPanelHidden, false)
  const personalAlerts = useChartAlerts(user?.username, activeTicker)
  const oiExchangeOptions = useExchangeOptions('open_interest')
  const chartExchangeOptions = useExchangeOptions('futures_ticker')
  const orderbookExchangeOptions = useExchangeOptions('orderbook')

  const [searchParams] = useSearchParams()
  const tickerParam = searchParams.get('ticker')
  useScreenerTickerParam({ tickerParam, tickers, hasLoadedTickers, setActiveTicker, addTicker })

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('separate')

  // Selected exchanges for display
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>(FALLBACK_EXCHANGES)
  const [priceExchange, setPriceExchange] = useState('')
  const [discoveryOpen, setDiscoveryOpen] = useState(true)
  const [discoveryFilters, setDiscoveryFilters] = useState<ScreenerDiscoveryFilters>(() => defaultDiscoveryFilters(FALLBACK_EXCHANGES))
  const setDiscoveryExchanges = useCallback((exchanges: string[]) => {
    setDiscoveryFilters((current) => ({ ...current, exchanges, minMatchingExchanges: Math.min(current.minMatchingExchanges, Math.max(1, exchanges.length)) }))
  }, [])

  const [selectedOrderbookSourceIds, setSelectedOrderbookSourceIds] = useState<string[]>([])
  const [collapsedOrderbookSourceIds, setCollapsedOrderbookSourceIds] = useState<string[]>([])
  const [orderbookCompression, setOrderbookCompression] = useState<OrderbookCompression>(DEFAULT_ORDERBOOK_COMPRESSION)
  const [orderbookCompressionOverrides, setOrderbookCompressionOverrides] = useState<Record<string, OrderbookCompression>>({})

  const [sidebarTab, setSidebarTab] = useState<ScreenerSidebarTab>('discovery')

  // Period selector popover state
  const [periodPopoverOpen, setPeriodPopoverOpen] = useState(false)

  const hydratePreferences = useCallback(() => {
    try {
      const savedMode = localStorage.getItem(storageKeys.mode)
      setViewMode(savedMode === 'merged' || savedMode === 'aggregated' ? 'merged' : 'separate')

      setPriceExchange(localStorage.getItem(storageKeys.priceExchange) || '')
      setDiscoveryOpen(localStorage.getItem(storageKeys.discoveryOpen) !== 'false')
      setSidebarTab(localStorage.getItem(storageKeys.sidebarTab) === 'history' ? 'history' : 'discovery')
      setDiscoveryFilters(parseDiscoveryFilters(localStorage.getItem(storageKeys.discoveryFilters), oiExchangeOptions))

      const savedOrderbookPanelHidden = localStorage.getItem(storageKeys.orderbookPanelHidden)
      setOrderbookPanelHidden(savedOrderbookPanelHidden == null || savedOrderbookPanelHidden === 'true')

      const savedOrderbookConfig = parseScreenerOrderbookPrefs(
        localStorage.getItem(storageKeys.orderbookConfig),
        localStorage.getItem(storageKeys.orderbookSources),
      )
      setSelectedOrderbookSourceIds(savedOrderbookConfig.selectedSourceIds)
      setCollapsedOrderbookSourceIds(savedOrderbookConfig.collapsedSourceIds)
      setOrderbookCompression(savedOrderbookConfig.compression)
      setOrderbookCompressionOverrides(savedOrderbookConfig.compressionOverrides)
    } catch {
      setViewMode('separate')
      setPriceExchange('')
      setDiscoveryOpen(true)
      setSidebarTab('discovery')
      setDiscoveryFilters(defaultDiscoveryFilters(oiExchangeOptions))
      setOrderbookPanelHidden(true)
      setSelectedOrderbookSourceIds([])
      setCollapsedOrderbookSourceIds([])
      setOrderbookCompression(DEFAULT_ORDERBOOK_COMPRESSION)
      setOrderbookCompressionOverrides({})
    }
  }, [storageKeys, oiExchangeOptions])
  const hasHydratedPrefs = useScreenerPreferenceHydration(storageKeys.discoveryFilters, hydratePreferences)
  const chartPresets = useScreenerChartPresets({
    user, orderStorageKey: storageKeys.chartOrder, panelWidthsStorageKey: storageKeys.panelWidths,
    mode: viewMode, onModeChange: setViewMode, priceExchange, onPriceExchangeChange: setPriceExchange,
    leftOpen: discoveryOpen, onLeftOpenChange: setDiscoveryOpen,
    rightOpen: !orderbookPanelHidden, onRightOpenChange: (open) => setOrderbookPanelHidden(!open),
    sidebarTab, onSidebarTabChange: setSidebarTab, disabled: !hasHydratedPrefs,
  })
  const orderbookPresets = useScreenerOrderbookPresets(user, {
    selectedSourceIds: selectedOrderbookSourceIds, collapsedSourceIds: collapsedOrderbookSourceIds,
    compression: orderbookCompression, compressionOverrides: orderbookCompressionOverrides,
  }, (value) => {
    setSelectedOrderbookSourceIds(value.selectedSourceIds)
    setCollapsedOrderbookSourceIds(value.collapsedSourceIds)
    setOrderbookCompression(value.compression)
    setOrderbookCompressionOverrides(value.compressionOverrides)
  }, hasHydratedPrefs)

  // Persist view mode
  useEffect(() => {
    if (!hasHydratedPrefs) return
    try {
      localStorage.setItem(storageKeys.mode, viewMode)
    } catch {
      // No-op
    }
  }, [viewMode, storageKeys, hasHydratedPrefs])

  useEffect(() => {
    if (!hasHydratedPrefs) return
    try {
      localStorage.setItem(storageKeys.priceExchange, priceExchange)
      localStorage.setItem(storageKeys.discoveryOpen, String(discoveryOpen))
      localStorage.setItem(storageKeys.sidebarTab, sidebarTab)
      localStorage.setItem(storageKeys.discoveryFilters, JSON.stringify(discoveryFilters))
    } catch {
      // No-op
    }
  }, [priceExchange, discoveryOpen, sidebarTab, discoveryFilters, storageKeys, hasHydratedPrefs])

  // Persist orderbook panel visibility
  useEffect(() => {
    if (!hasHydratedPrefs) return
    try {
      localStorage.setItem(storageKeys.orderbookPanelHidden, String(orderbookPanelHidden))
    } catch {
      // No-op
    }
  }, [orderbookPanelHidden, storageKeys, hasHydratedPrefs])

  // Keep collapsed sources aligned with selected sources.
  useEffect(() => {
    setCollapsedOrderbookSourceIds((prev) =>
      prev.filter((sourceId) => selectedOrderbookSourceIds.includes(sourceId))
    )
  }, [selectedOrderbookSourceIds])

  // Persist selected/collapsed orderbook source preferences
  useEffect(() => {
    if (!hasHydratedPrefs) return
    try {
      localStorage.setItem(
        storageKeys.orderbookConfig,
        JSON.stringify({
          selectedSourceIds: selectedOrderbookSourceIds,
          collapsedSourceIds: collapsedOrderbookSourceIds,
          compression: orderbookCompression,
          compressionOverrides: orderbookCompressionOverrides,
        } satisfies ScreenerOrderbookPrefs)
      )
      localStorage.setItem(storageKeys.orderbookSources, JSON.stringify(selectedOrderbookSourceIds))
    } catch {
      // No-op
    }
  }, [selectedOrderbookSourceIds, collapsedOrderbookSourceIds, orderbookCompression, orderbookCompressionOverrides, storageKeys, hasHydratedPrefs])

  // Handle period change
  const handlePeriodChange = async (period: string) => {
    await updateSettings({ period })
    setPeriodPopoverOpen(false)
  }

  // Global paste shortcut
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      if (isLoading) return

      const target = event.target as HTMLElement | null
      const tagName = target?.tagName?.toLowerCase()
      const isEditable = Boolean(
        target &&
        (tagName === 'input' || tagName === 'textarea' || target.isContentEditable)
      )
      if (isEditable) return

      const text = event.clipboardData?.getData('text') || ''
      const ticker = extractTickerFromPastedText(text)
      if (!ticker) return

      event.preventDefault()
      void addTicker(ticker)
    }

    window.addEventListener('paste', onPaste)
    return () => window.removeEventListener('paste', onPaste)
  }, [addTicker, isLoading])

  // OI charts only include exchanges with real stored OI history.
  const availableExchanges = useMemo(() => {
    if (!activeTickerData?.exchanges) return []
    return Object.entries(activeTickerData.exchanges)
      .filter(([, data]) => (data.oi_history?.length || 0) > 0)
      .map(([exchange]) => exchange)
      .sort()
  }, [activeTickerData])

  const effectivePriceExchange = resolveScreenerPriceExchange(priceExchange, availableExchanges)
  const chartParseExchanges = settings.chart_filters?.exchanges || settings.exchanges || chartExchangeOptions
  const chartParseMarkets = settings.chart_filters?.markets || settings.markets || DEFAULT_MARKETS
  const orderbookParseExchanges = settings.orderbook_filters?.exchanges || settings.exchanges || orderbookExchangeOptions
  const orderbookParseMarkets = settings.orderbook_filters?.markets || settings.markets || DEFAULT_MARKETS
  const chartFilters = useMemo(() => ({ exchanges: chartParseExchanges, markets: chartParseMarkets }),
    [chartParseExchanges, chartParseMarkets])
  const orderbookFilters = useMemo(() => ({ exchanges: orderbookParseExchanges, markets: orderbookParseMarkets }),
    [orderbookParseExchanges, orderbookParseMarkets])
  const filterDefaults = useScreenerFilterDefaults({
    user, selectedExchanges, setSelectedExchanges, chartFilters, orderbookFilters,
    hasLoadedSettings: hasLoadedTickers, updateSettings,
    discoveryExchanges: discoveryFilters.exchanges, setDiscoveryExchanges,
    hasHydratedDiscoveryPrefs: hasHydratedPrefs,
  })
  const discoveryFilterDefaults = useScreenerDiscoveryDefaults(user, discoveryFilters, setDiscoveryFilters, hasHydratedPrefs)
  const filterPresets = useScreenerFilterPresets({
    user, selectedExchanges, setSelectedExchanges, availableExchanges, chartFilters, orderbookFilters,
    updateParseFilters: filterDefaults.updateParseFilters, parseDisabled: filterDefaults.parseDisabled,
    filters: discoveryFilters, setFilters: setDiscoveryFilters, hydrated: hasHydratedPrefs,
  })
  useScreenerExchangeSelection({
    savedExchanges: filterPresets.displayExchangesOverride ?? filterDefaults.savedExchanges,
    preferenceKey: JSON.stringify([user?.username, filterPresets.displayExchangesOverride ?? null]),
    activeTicker,
    dataTicker: activeTickerData?.symbol,
    availableExchanges,
    hasHydratedPrefs,
    setSelectedExchanges,
  })

  const configuredOrderbookSourceIds = useMemo(() => {
    const sourceIds = new Set<string>()
    for (const exchange of orderbookParseExchanges) {
      for (const market of orderbookParseMarkets) {
        sourceIds.add(`${String(exchange).toLowerCase()}:${String(market).toLowerCase()}`)
      }
    }
    return Array.from(sourceIds)
  }, [orderbookParseExchanges, orderbookParseMarkets])

  // Helper to format numbers
  function formatNumber(num: number): string {
    if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(2) + 'B'
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + 'M'
    if (num >= 1_000) return (num / 1_000).toFixed(2) + 'K'
    return num.toFixed(2)
  }

  const handleDiscoveryTickerSelect = async (symbol: string) => {
    const normalized = symbol.trim().toUpperCase()
    if (!normalized) return
    const exists = tickers.some((ticker) => ticker.symbol === normalized)
    if (exists) {
      setActiveTicker(normalized)
      return
    }
    // addTicker selects the backend-normalized symbol after refreshing History.
    await addTicker(normalized)
  }

  // Current period label
  const currentPeriodLabel = PERIODS.find(p => p.value === settings.period)?.label || settings.period.toUpperCase()

  // Compute stats for the active ticker
  const tickerStats = useMemo(() => {
    if (!activeTickerData?.exchanges) return null
    let totalOI = 0
    let totalOIUsdt = 0

    for (const data of Object.values(activeTickerData.exchanges)) {
      if (data.oi_current) totalOI += data.oi_current
      if (data.oi_current_usdt) totalOIUsdt += data.oi_current_usdt
    }
    const selectedPriceData = activeTickerData.exchanges[effectivePriceExchange]
    const priceHistory = selectedPriceData?.oi_history || []
    const latestAlignedPrice = priceHistory.length > 0 ? priceHistory[priceHistory.length - 1]?.price : null
    const price = selectedPriceData?.last_price || latestAlignedPrice || null
    return { price, totalOI, totalOIUsdt }
  }, [activeTickerData, effectivePriceExchange])

  const liveOIStatus = useMemo<LiveOIStatusSummary | null>(() => {
    const isLiveFiveSecondView = settings.period === '5s'
      && (settings.timeframe?.mode ?? 'live') === 'live'
    const status = activeTickerData?.live_oi
    if (!isLiveFiveSecondView || !status || !activeTickerData) return null

    const healthy: string[] = []
    const waiting: string[] = []
    const unchanged: string[] = []
    for (const exchange of status.collecting) {
      const data = findExchangeData(activeTickerData.exchanges, exchange)
      const history = data?.oi_history || []
      const latestPoint = history[history.length - 1]
      const sampleKind = String(
        data?.oi_sample_kind ?? latestPoint?.sampleKind ?? '',
      ).toLowerCase()
      if (!data || data.oi_current == null || sampleKind !== 'continuous') {
        waiting.push(exchange)
        continue
      }
      const age = data.oi_source_age_seconds
      const quality = data.oi_quality ?? latestPoint?.quality
      const sourceIsCurrent = typeof age === 'number'
        && Number.isFinite(age)
        && age <= LIVE_OI_STALE_AFTER_SECONDS
      if (!isFreshOI({ quality }) || !sourceIsCurrent) {
        unchanged.push(exchange)
      } else {
        healthy.push(exchange)
      }
    }

    const issues: string[] = []
    if (!status.enabled) {
      issues.push('OI collector disabled')
    } else {
      if (status.unsupported.length > 0) {
        issues.push(`5s OI unsupported: ${displayExchangeList(status.unsupported)}`)
      }
      if (status.disabled.length > 0) {
        issues.push(`collector disabled: ${displayExchangeList(status.disabled)}`)
      }
      if (status.unavailable.length > 0) {
        issues.push(`symbol unavailable: ${displayExchangeList(status.unavailable)}`)
      }
      if (waiting.length > 0) {
        issues.push(`waiting for 5s sample: ${displayExchangeList(waiting)}`)
      }
      if (unchanged.length > 0) {
        issues.push(`source unchanged or stale: ${displayExchangeList(unchanged)}`)
      }

    }

    return {
      enabled: status.enabled,
      requestedCount: status.requested.length,
      collectingCount: status.collecting.length,
      healthy,
      waiting,
      unchanged,
      issues,
    }
  }, [activeTickerData, availableExchanges, selectedExchanges, settings.period, settings.timeframe?.mode])

  return (
    <div className="flex min-h-0 h-full flex-col overflow-hidden bg-background text-foreground">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-1.5 border-b border-border/40 bg-background/80 backdrop-blur-sm z-10 h-12 shrink-0">
        {/* Left section */}
        <div className="flex min-w-0 flex-1 items-center gap-3 overflow-x-auto py-1 [&>*]:shrink-0">
          {/* Ticker Selector + Period (unified control) */}
          <div className="flex items-center">
            <ScreenerTickerPicker
              activeTicker={activeTicker}
              tickers={tickers}
              exchangeOptions={oiExchangeOptions}
              disabled={!hasLoadedTickers || isLoading}
              onSelectTicker={setActiveTicker}
              onAddTicker={addTicker}
              onOpenHistory={() => { setDiscoveryOpen(true); setSidebarTab('history') }}
            />

            {/* Period selector (attached to ticker) */}
            <Popover open={periodPopoverOpen} onOpenChange={setPeriodPopoverOpen}>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    "flex items-center gap-1 px-2.5 py-1.5 rounded-r-md text-sm font-semibold transition-colors",
                    "hover:bg-muted/30 border border-border/50 text-muted-foreground hover:text-foreground",
                    "border-l-0"
                  )}
                >
                  {currentPeriodLabel}
                  <ChevronDown className="h-3 w-3" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-1" align="start">
                <div className="flex flex-col gap-0.5">
                  {PERIODS.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => handlePeriodChange(p.value)}
                      className={cn(
                        "px-4 py-1.5 text-xs font-medium rounded-sm transition-colors text-left",
                        settings.period === p.value
                          ? "bg-primary text-primary-foreground"
                          : "hover:bg-muted/50 text-foreground"
                      )}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            {activeTicker && <CopyTickerButton symbol={activeTicker} className="ml-1" />}
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-border/40" />

          {/* Exchange badges (for active ticker) */}
          {availableExchanges.length > 0 && (
            <div className="flex gap-1">
              {availableExchanges.map(ex => (
                <div key={ex} className="px-1.5 py-0.5 text-[9px] font-bold uppercase text-muted-foreground border border-border/60 rounded bg-background/50">
                  {ex.substring(0, 3)}
                </div>
              ))}
            </div>
          )}

          {/* Divider */}
          {availableExchanges.length > 0 && <div className="h-6 w-px bg-border/40" />}

          {/* Timeframe */}
          <TimeframeSelector
            value={settings.timeframe}
            onChange={(tf) => void updateSettings({ timeframe: tf })}
          />

          {/* Price source */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 max-w-36 gap-1.5 border-border/50 bg-muted/10 px-2 text-[10px]">
                <span className="text-muted-foreground">Price</span>
                <span className="truncate capitalize text-foreground">{effectivePriceExchange || '—'}</span>
                <ChevronDown className="h-3 w-3 text-muted-foreground" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-44 p-1" align="start">
              {availableExchanges.map((exchange) => (
                <button
                  key={exchange}
                  type="button"
                  onClick={() => setPriceExchange(exchange)}
                  className={cn(
                    'w-full rounded-sm px-2.5 py-1.5 text-left text-xs capitalize transition-colors',
                    effectivePriceExchange === exchange ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/60',
                  )}
                >
                  {exchange}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Mode */}
          <div className="flex bg-muted/20 rounded p-0.5 border border-border/50">
            <button
              onClick={() => setViewMode('separate')}
              className={cn(
                "px-2 py-0.5 text-[10px] font-medium rounded-sm transition-colors uppercase tracking-wide",
                viewMode === 'separate' ? "bg-primary text-primary-foreground" : "hover:text-foreground text-muted-foreground"
              )}
            >
              Separate OI
            </button>
            <button
              onClick={() => setViewMode('merged')}
              className={cn(
                "px-3 py-0.5 text-[10px] font-medium rounded-sm transition-colors uppercase tracking-wide",
                viewMode === 'merged' ? "bg-primary text-primary-foreground" : "hover:text-foreground text-muted-foreground"
              )}
            >
              Merged OI
            </button>
          </div>

          <ScreenerFilterControls
            availableExchanges={availableExchanges}
            selectedExchanges={selectedExchanges}
            onSelectedExchangesChange={filterPresets.onDisplayExchangesChange}
            chartExchangeOptions={chartExchangeOptions}
            orderbookExchangeOptions={orderbookExchangeOptions}
            chartFilters={chartFilters}
            orderbookFilters={orderbookFilters}
            onParseFiltersChange={filterDefaults.updateParseFilters}
            parseDisabled={filterDefaults.parseDisabled}
            displayDefaults={{ ...filterDefaults.displayDefaults, presets: filterPresets.display,
              onRestore: () => { filterPresets.clearDisplayOverride(); filterDefaults.displayDefaults.onRestore() },
            }}
            parseDefaults={{ ...filterDefaults.parseDefaults, presets: filterPresets.parse }}
          />
        </div>

        {/* Right section: Stats + Controls */}
        <div className="flex shrink-0 items-center gap-2 pl-2">
          <PersonalChartAlerts state={personalAlerts} onSelectSymbol={(symbol) => void handleDiscoveryTickerSelect(symbol)} />
          {/* Stats */}
          {tickerStats && (
            <div className="hidden items-center gap-5 2xl:flex">
              {tickerStats.price !== null && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] text-muted-foreground uppercase font-bold tracking-wider">Price</span>
                  <span className="text-xs font-mono font-medium">${tickerStats.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              )}
              {tickerStats.totalOI > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] text-muted-foreground uppercase font-bold tracking-wider">OI</span>
                  <span className="text-xs font-mono font-medium text-cyan-500">{formatNumber(tickerStats.totalOI)}</span>
                </div>
              )}
              {tickerStats.totalOIUsdt > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] text-muted-foreground uppercase font-bold tracking-wider">OI $</span>
                  <span className="text-xs font-mono font-medium text-green-500">${formatNumber(tickerStats.totalOIUsdt)}</span>
                </div>
              )}
            </div>
          )}

          <div className="h-5 w-px bg-border/40" />

          {liveOIStatus && (
            <Badge
              variant="outline"
              className={cn(
                'h-6 px-2 text-[9px] font-mono tracking-wide',
                liveOIStatus.issues.length > 0
                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-500'
                  : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500',
              )}
              title={liveOIStatus.issues.join(' · ') || `${liveOIStatus.collectingCount} continuous OI sources healthy`}
            >
              {liveOIStatus.enabled
                ? `OI 5S ${liveOIStatus.healthy.length}/${liveOIStatus.requestedCount}`
                : 'OI 5S DISABLED'}
            </Badge>
          )}

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setOrderbookPanelHidden((prev) => !prev)}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            title={orderbookPanelHidden ? 'Show orderbook panel' : 'Hide orderbook panel'}
          >
            {orderbookPanelHidden ? <PanelRight className="h-3.5 w-3.5" /> : <PanelRightClose className="h-3.5 w-3.5" />}
          </Button>

          {lastUpdated && (
            <span className="text-[10px] text-muted-foreground font-mono">
              {lastUpdated.toLocaleTimeString('en-GB', { timeZone: 'UTC', hour12: false })} UTC
            </span>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={refresh}
            disabled={isLoading}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isLoading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <ScreenerWorkspace
        storageKey={storageKeys.panelWidths}
        widths={chartPresets.panelWidths}
        onWidthsChange={chartPresets.onPanelWidthsChange}
        leftOpen={discoveryOpen}
        rightOpen={!orderbookPanelHidden}
        left={(
          <ScreenerSidebar
            open={discoveryOpen}
            onOpenChange={setDiscoveryOpen}
            tab={sidebarTab}
            onTabChange={setSidebarTab}
            historyCount={tickers.length}
            discovery={(
              <ScreenerDiscoveryPanel
                open={discoveryOpen && sidebarTab === 'discovery'}
                filters={discoveryFilters}
                exchangeOptions={oiExchangeOptions}
                exchangeDefaults={{ ...filterDefaults.discoveryDefaults, presets: filterPresets.discoveryExchanges }}
                filterDefaults={{ ...discoveryFilterDefaults, presets: filterPresets.discovery }}
                activeSymbol={activeTicker}
                onFiltersChange={setDiscoveryFilters}
                onSelectSymbol={(symbol) => void handleDiscoveryTickerSelect(symbol)}
              />
            )}
            history={(
              <ScreenerHistoryPanel
                tickers={tickers}
                activeTicker={activeTicker}
                onSelectTicker={setActiveTicker}
                onAddTicker={addTicker}
                onRemoveTicker={removeTicker}
                isLoading={isLoading}
              />
            )}
          />
        )}
        right={(
          <ScreenerOrderbookPanel
            presets={orderbookPresets}
            symbol={activeTicker || ''}
            tickerData={activeTickerData}
            selectedSourceIds={selectedOrderbookSourceIds}
            onSelectedSourceIdsChange={setSelectedOrderbookSourceIds}
            collapsedSourceIds={collapsedOrderbookSourceIds}
            onCollapsedSourceIdsChange={setCollapsedOrderbookSourceIds}
            compression={orderbookCompression}
            onCompressionChange={setOrderbookCompression}
            compressionOverrides={orderbookCompressionOverrides}
            onCompressionOverridesChange={setOrderbookCompressionOverrides}
            configuredSourceIds={configuredOrderbookSourceIds}
            availableExchanges={activeTickerData?.available_exchanges || []}
          />
        )}
      >
        {/* Charts Area - Full Width */}
        <div className="min-h-0 min-w-0 flex-1 flex flex-col overflow-hidden bg-background relative">
          <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border/40 px-3 py-2">
            <PresetControls label="chart layout" presets={chartPresets.presets} className="max-w-xl" />
          </div>
          {/* Charts Area */}
          <div className="flex-1 overflow-y-auto p-2 custom-scrollbar">
            {liveOIStatus && liveOIStatus.issues.length > 0 && (
              <div className="mb-2 px-3 py-2 bg-amber-500/10 border border-amber-500/25 rounded text-xs text-amber-500 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <span className="font-semibold">5s OI status:</span>{' '}
                  {liveOIStatus.issues.join(' · ')}
                </div>
              </div>
            )}
            {error && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            {!activeTicker ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <div className="text-center">
                  <div className="text-lg mb-2">No ticker selected</div>
                  <div className="text-sm">Search with Select Ticker or choose a symbol in Discovery or History</div>
                </div>
              </div>
            ) : (
              <ChartGrid
                includeOIOhlc={false}
                tickerData={activeTickerData}
                symbol={activeTicker || ''}
                mode={viewMode}
                selectedExchanges={selectedExchanges}
                priceExchange={effectivePriceExchange}
                interval={settings.period}
                timeframe={settings.timeframe}
                onGoToLatest={() => void updateSettings({ timeframe: { mode: 'live' } })}
                orderStorageKey={storageKeys.chartOrder}
                layoutPreferences={chartPresets.layoutPreferences}
                alertControls={personalAlerts.controls}
              />
            )}
          </div>
        </div>

      </ScreenerWorkspace>
    </div>
  )
}
